import { type FC, type ReactNode, useEffect, useRef } from 'react';

import { XMarkIcon } from '@heroicons/react/24/outline';
import type { Editor } from '@tiptap/react';

import { AutoGrowTextarea } from '@/ui/AutoGrowTextarea';

import { CommunityWebPostEmbedCard } from '../../../communityWebPostEmbedCard/CommunityWebPostEmbedCard';
import type { ComposerAttachableLists, ComposerQuotedPost } from '../../CommunityPostComposer.type';
import { QuotedPostPreview } from '../../QuotedPostPreview';
import { tr } from '../constants';
import { LinkPromptMenu } from '../nodes/LinkPrompt/LinkPromptMenu';
import { SelectionFormatMenu } from '../toolbar/SelectionFormatMenu';
import type { VideoDropHandlers } from '../useVideoDrop';
import { ComposerEditor } from './ComposerEditor';
import { ComposerFrameBody } from './ComposerFrame';

interface ComposerBodyProps {
  editor: Editor | null;
  title: string;
  onTitleChange: (title: string) => void;
  onKeyboardSubmit: () => void;
  showTitleInput: boolean;
  autoFocusTitle?: boolean;
  titleMaxLength?: number;
  editorMinHeight?: number;
  quotedPost?: ComposerQuotedPost;
  onQuotedPostRemove?: () => void;
  attached?: ComposerAttachableLists;
  onAttachedChange?: (next: ComposerAttachableLists) => void;
  transformImageUrl?: (url: string, width: number) => string;
  error?: string | null;
  belowContent?: ReactNode;
  translations?: Record<string, string>;
  /** Native video drop, scoped to the editor region (excludes the title field). */
  videoDrop?: { handlers: VideoDropHandlers; isActive: boolean; overlay: ReactNode };
}

const RemoveAttachmentButton: FC<{ onClick: () => void; translations?: Record<string, string> }> = ({
  onClick,
  translations,
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={tr(translations, 'community.composer.attached_post_remove')}
    className="absolute top-2 right-2 size-7 rounded-full flex items-center justify-center bg-white/90 shadow-sm hover:bg-white transition-colors"
  >
    <XMarkIcon className="size-4" aria-hidden="true" />
  </button>
);

export const ComposerBody: FC<ComposerBodyProps> = ({
  editor,
  title,
  onTitleChange,
  onKeyboardSubmit,
  showTitleInput,
  autoFocusTitle = true,
  titleMaxLength,
  editorMinHeight,
  quotedPost,
  onQuotedPostRemove,
  attached,
  onAttachedChange,
  transformImageUrl,
  error,
  belowContent,
  translations,
  videoDrop,
}) => {
  const attachedPosts = attached?.posts ?? [];
  const titleInputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!showTitleInput || !autoFocusTitle) return;
    queueMicrotask(() => titleInputRef.current?.focus());
  }, [showTitleInput, autoFocusTitle]);

  return (
    <ComposerFrameBody className="justify-between">
      <div className="flex flex-col gap-4 flex-1 overflow-y-auto">
        {showTitleInput && (
          <AutoGrowTextarea
            ref={titleInputRef}
            maxRows={3}
            singleLine
            onSubmitShortcut={onKeyboardSubmit}
            value={title}
            onChange={(e) => onTitleChange(e.target.value)}
            maxLength={titleMaxLength}
            placeholder={tr(translations, 'community.composer.post_title_placeholder')}
            className="w-full shrink-0 p-0 text-[28px] focus:text-[28px] font-semibold leading-8 tracking-[-0.56px] text-black placeholder:text-black/30 outline-none border-none bg-transparent focus:ring-0"
          />
        )}

        <div className="relative flex flex-1 flex-col gap-4" {...(videoDrop?.handlers ?? {})}>
          {videoDrop?.isActive && videoDrop.overlay}

          <ComposerEditor editor={editor} minHeight={editorMinHeight} />
          {editor && <LinkPromptMenu editor={editor} translations={translations} />}
          {editor && <SelectionFormatMenu editor={editor} translations={translations} />}

          {attachedPosts.map((post) => (
            <div key={post.id} className="relative">
              <CommunityWebPostEmbedCard post={post} transformImageUrl={transformImageUrl} />
              {onAttachedChange && (
                <RemoveAttachmentButton
                  onClick={() =>
                    onAttachedChange({ ...attached, posts: attachedPosts.filter((p) => p.id !== post.id) })
                  }
                  translations={translations}
                />
              )}
            </div>
          ))}

          {quotedPost && (
            <div className="relative">
              <QuotedPostPreview post={quotedPost} />
              {onQuotedPostRemove && (
                <RemoveAttachmentButton onClick={onQuotedPostRemove} translations={translations} />
              )}
            </div>
          )}
        </div>
      </div>

      {belowContent}

      {error && (
        <div className="pb-2">
          <p className="text-[13px] leading-4 text-red-500">{error}</p>
        </div>
      )}
    </ComposerFrameBody>
  );
};
